import { useState } from 'react';
import { ChevronDown, ChevronRight, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { TextRow, HtmlField, ImageField, Panel, RowTools, AddButton } from './EditorFields';

export const SECTION_TYPES = {
  text: 'Text + Image', list: 'Bullet List', checklist: 'Checklist (benefits)', chips: 'Chips (dark band)',
  steps: 'Numbered Steps', highlight: 'Highlight Banner (green)', cta: 'Call-to-Action Band',
};

const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now() + Math.random()));

const ItemsEditor = ({ items, onChange, testId }) => {
  const upd = (i, k, v) => onChange(items.map((it, j) => (j === i ? { ...it, [k]: v } : it)));
  return (
    <div className="space-y-2">
      <Label className="text-sm">Items</Label>
      {items.map((it, i) => (
        <div key={i} className="flex gap-2">
          <Input value={it.title || ''} onChange={(e) => upd(i, 'title', e.target.value)} placeholder="Title" data-testid={`${testId}-title-${i}`} />
          <Input value={it.text || ''} onChange={(e) => upd(i, 'text', e.target.value)} placeholder="Description (optional)" />
          <RowTools list={items} i={i} onChange={onChange} testId={testId} />
        </div>
      ))}
      <AddButton label="Add item" onClick={() => onChange([...items, { title: '', text: '' }])} testId={`${testId}-add`} />
    </div>
  );
};

const ButtonsEditor = ({ buttons, onChange, testId }) => {
  const upd = (i, k, v) => onChange(buttons.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  return (
    <div className="space-y-2">
      <Label className="text-sm">Buttons</Label>
      {buttons.map((b, i) => (
        <div key={i} className="flex flex-wrap gap-2">
          <Input value={b.label || ''} onChange={(e) => upd(i, 'label', e.target.value)} placeholder="Label" className="w-48" data-testid={`${testId}-label-${i}`} />
          <select value={b.action} onChange={(e) => upd(i, 'action', e.target.value)} className="h-10 rounded-md border px-2 text-sm" data-testid={`${testId}-action-${i}`}>
            <option value="candidate">Job Seeker Registration</option><option value="employer">Employer Registration</option><option value="url">Custom Link</option>
          </select>
          {b.action === 'url' && <Input value={b.url || ''} onChange={(e) => upd(i, 'url', e.target.value)} placeholder="https://" className="flex-1 min-w-[10rem]" />}
          <RowTools list={buttons} i={i} onChange={onChange} testId={testId} />
        </div>
      ))}
      <AddButton label="Add button" onClick={() => onChange([...buttons, { label: 'Register Now', action: 'candidate', url: '' }])} testId={`${testId}-add`} />
    </div>
  );
};

const SectionCard = ({ f, s, i, list, onChange }) => {
  const [open, setOpen] = useState(false);
  const upd = (k) => (v) => onChange(list.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  const tid = `editor-section-${i}`;
  return (
    <div className="border rounded-xl bg-white" data-testid={tid}>
      <div className="flex items-center gap-2 p-3">
        <button type="button" onClick={() => setOpen(!open)} className="flex-1 flex items-center gap-2 text-left min-w-0" data-testid={`${tid}-toggle`}>
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          <span className="text-xs px-2 py-0.5 rounded bg-teal-50 text-teal-700 font-medium shrink-0">{SECTION_TYPES[s.type] || s.type}</span>
          <span className="font-medium truncate">{s.title || 'Untitled section'}</span>
          {s.visible === false && <EyeOff className="w-4 h-4 text-slate-400" />}
        </button>
        <Switch checked={s.visible !== false} onCheckedChange={upd('visible')} data-testid={`${tid}-visible`} />
        <RowTools list={list} i={i} onChange={onChange} testId="editor-section" />
      </div>
      {open && (
        <div className="border-t p-4 space-y-4">
          <div className="grid md:grid-cols-3 gap-3">
            <div><Label className="text-sm">Layout</Label>
              <select value={s.type} onChange={(e) => upd('type')(e.target.value)} className="w-full h-10 rounded-md border px-2 text-sm" data-testid={`${tid}-type`}>
                {Object.entries(SECTION_TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select></div>
            <TextRow label="Title" value={s.title} onChange={upd('title')} testId={`${tid}-title`} />
            <TextRow label="Subtitle / Eyebrow" value={s.subtitle} onChange={upd('subtitle')} testId={`${tid}-subtitle`} />
          </div>
          <HtmlField label="Content (HTML)" value={s.content_html} onChange={upd('content_html')} testId={`${tid}-content`} />
          <div className="grid md:grid-cols-[1fr_12rem] gap-3 items-end">
            <ImageField fairId={f.id} label="Image (optional)" value={s.image} onChange={upd('image')} testId={`${tid}-image`} />
            <div><Label className="text-sm">Image Position</Label>
              <select value={s.image_position || 'right'} onChange={(e) => upd('image_position')(e.target.value)} className="w-full h-10 rounded-md border px-2 text-sm">
                <option value="right">Right</option><option value="left">Left</option>
              </select></div>
          </div>
          {s.type !== 'text' && <ItemsEditor items={s.items || []} onChange={upd('items')} testId={`${tid}-item`} />}
          <ButtonsEditor buttons={s.buttons || []} onChange={upd('buttons')} testId={`${tid}-button`} />
        </div>
      )}
    </div>
  );
};

export const SectionsTab = ({ f, set }) => {
  const list = f.sections || [];
  return (
    <Panel title="Landing Page Sections" desc="Sections appear on the public page in this order. Toggle to hide without deleting.">
      <div className="space-y-3">
        {list.map((s, i) => <SectionCard key={s.id || i} f={f} s={s} i={i} list={list} onChange={set('sections')} />)}
      </div>
      <AddButton label="Add section" testId="editor-section-add"
        onClick={() => set('sections')([...list, { id: uid(), type: 'text', title: 'New Section', subtitle: '', content_html: '', image: '', image_position: 'right', items: [], buttons: [], visible: true }])} />
    </Panel>
  );
};
